import Script from 'next/script'

type GoogleTagManagerProps = {
  ids?: string[]
}

/**
 * Validates that a tag ID matches a known Google tag format.
 * Rejects malformed IDs that could break inline script string literals.
 */
const isValidGoogleTagId = (id: string) => /^(GTM|G|AW|DC|UA)-[A-Za-z0-9_-]+$/i.test(id)

const normalizeIds = (ids?: Array<string | null> | null) =>
  Array.from(new Set((ids ?? []).map((id) => id?.trim() ?? '').filter((id) => id.length > 0)))

const isGoogleTagManagerContainerId = (id: string) => id.toUpperCase().startsWith('GTM-')

export const partitionGoogleTagIds = (ids?: Array<string | null> | null) => {
  const normalizedIds = normalizeIds(ids).filter(isValidGoogleTagId)

  return {
    gtmIds: normalizedIds.filter(isGoogleTagManagerContainerId),
    googleTagIds: normalizedIds.filter((id) => !isGoogleTagManagerContainerId(id)),
  }
}

export function GoogleTagManager({ids}: GoogleTagManagerProps) {
  const {gtmIds, googleTagIds} = partitionGoogleTagIds(ids)

  if (!gtmIds.length && !googleTagIds.length) return null

  return (
    <>
      {googleTagIds.length ? (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
              googleTagIds[0],
            )}`}
            strategy="afterInteractive"
          />
          <Script
            id="google-tag-config"
            strategy="afterInteractive"
          >{`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
${googleTagIds.map((id) => `gtag('config', '${id}');`).join('\n')}`}</Script>
        </>
      ) : null}
      {gtmIds.map((id) => (
        <Script
          key={`gtm-${id}`}
          id={`gtm-${id}`}
          strategy="afterInteractive"
        >{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${id}');`}</Script>
      ))}
      {gtmIds.map((id) => (
        <noscript key={`gtm-ns-${id}`}>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${id}`}
            height="0"
            width="0"
            style={{display: 'none', visibility: 'hidden'}}
            title="Google Tag Manager"
          />
        </noscript>
      ))}
    </>
  )
}
